import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtDate, fmtINR } from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Minus, Plus, MapPin, Clock, Calendar } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

import OrganizerEvents from "@/pages/OrganizerEvents";

export default function EventDetail() {
  const { slug, organizerSlug } = useParams();
  const nav = useNavigate();
  const [event, setEvent] = useState(null);
  const [isOrganizerSlug, setIsOrganizerSlug] = useState(false);
  const [qty, setQty] = useState({});
  const [coupon, setCoupon] = useState("");
  const [loading, setLoading] = useState(false);

  const activeSlug = slug || organizerSlug;

  const loadEvent = () => {
    if (!activeSlug) return;
    api.get(`/events/${activeSlug}`)
      .then((r) => setEvent(r.data))
      .catch(() => {
        // If event not found, test if it's an organizer slug like /events/offclub
        api.get(`/organizers/${activeSlug}`)
          .then((r) => {
            if (r.data?.slug) {
              setIsOrganizerSlug(true);
            }
          })
          .catch(() => {});
      });
  };

  useEffect(() => {
    loadEvent();
    const handleReady = () => loadEvent();
    window.addEventListener("cgc:server-ready", handleReady);
    return () => window.removeEventListener("cgc:server-ready", handleReady);
  }, [activeSlug]);

  if (isOrganizerSlug) {
    return <OrganizerEvents />;
  }

  if (!event) return <PublicLayout><div className="max-w-6xl mx-auto p-10 text-center text-stone-500">Loading event details…</div></PublicLayout>;

  const setQ = (tid, v) => setQty((s) => ({ ...s, [tid]: Math.max(0, v) }));
  const subtotal = event.tiers.reduce((sum, t) => sum + (qty[t.id] || 0) * t.price_paise, 0);
  const hasItems = Object.values(qty).some((n) => n > 0);

  const reserve = async () => {
    if (!hasItems) return toast.error("Select at least one ticket");
    setLoading(true);
    try {
      const items = event.tiers.filter((t) => (qty[t.id] || 0) > 0).map((t) => ({ tier_id: t.id, qty: qty[t.id] }));
      const r = await api.post("/bookings/reserve", { event_id: event.id, items, coupon_code: coupon || null });
      nav(`/checkout/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reserve");
    } finally { setLoading(false); }
  };

  return (
    <PublicLayout>
      <div className="relative h-[46vh] min-h-[320px] w-full overflow-hidden">
        <img src={event.cover_image} alt={event.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950/70 via-stone-950/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-white">
          {event.organizer_slug && event.organizer_slug !== "cgc" ? (
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-white/20 backdrop-blur-md text-white mb-3">
              <span>Hosted by <b className="text-amber-300">{event.organizer}</b></span>
              <span className="opacity-60">·</span>
              <span className="opacity-80">Ticketing via Common Ground</span>
            </div>
          ) : (
            <div className="eyebrow text-amber-300">{event.category}</div>
          )}
          <h1 className="font-display text-3xl sm:text-5xl font-extrabold mt-1 max-w-3xl">{event.title}</h1>
          {event.subtitle && <p className="mt-2 text-lg max-w-2xl opacity-90">{event.subtitle}</p>}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 space-y-8">
          <div className="flex flex-wrap gap-6 text-sm">
            <div className="flex items-center gap-2"><Calendar size={16} /> {fmtDate(event.start_at)}</div>
            <div className="flex items-center gap-2"><Clock size={16} /> Ends {fmtDate(event.end_at)}</div>
            <div className="flex items-center gap-2"><MapPin size={16} /> {event.venue_name || "Online"}, {event.city}</div>
          </div>

          <div>
            <div className="eyebrow mb-3">About</div>
            <p className="text-stone-700 dark:text-stone-300 leading-relaxed">{event.description}</p>
          </div>

          {event.agenda?.length > 0 && (
            <div>
              <div className="eyebrow mb-3">Agenda</div>
              <ol className="space-y-3">
                {event.agenda.map((a, i) => (
                  <li key={i} className="flex gap-4 border-l-2 border-[color:var(--brand-terracotta)] pl-4">
                    <div className="font-mono text-sm text-[color:var(--brand-terracotta)] w-14">{a.time}</div>
                    <div>
                      <div className="font-semibold">{a.title}</div>
                      {a.description && <div className="text-sm text-stone-600 dark:text-stone-400">{a.description}</div>}
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {event.inclusions?.length > 0 && (
            <div>
              <div className="eyebrow mb-3">What&apos;s included</div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                {event.inclusions.map((i, idx) => (
                  <li key={idx} className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-[color:var(--brand-terracotta)] rounded-full" /> {i}</li>
                ))}
              </ul>
            </div>
          )}

          {event.faqs?.length > 0 && (
            <div>
              <div className="eyebrow mb-3">FAQ</div>
              <Accordion type="single" collapsible className="w-full">
                {event.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`f${i}`}>
                    <AccordionTrigger data-testid={`faq-${i}`}>{f.q}</AccordionTrigger>
                    <AccordionContent className="text-stone-600 dark:text-stone-400">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}

          {event.refund_terms && (
            <div className="border border-border rounded-lg p-4 bg-secondary/40">
              <div className="eyebrow mb-1">Refund terms</div>
              <p className="text-sm text-stone-700 dark:text-stone-300">{event.refund_terms}</p>
            </div>
          )}

          {(event.google_maps_url || (event.latitude && event.longitude)) && (
            <div>
              <div className="eyebrow mb-3">Getting there</div>
              <div className="cgc-card overflow-hidden">
                <iframe
                  title="venue-map"
                  data-testid="event-map"
                  src={event.latitude && event.longitude
                    ? `https://www.google.com/maps?q=${event.latitude},${event.longitude}&z=14&output=embed`
                    : event.google_maps_url}
                  width="100%"
                  height="280"
                  loading="lazy"
                  style={{ border: 0 }}
                />
                <div className="p-4 flex items-center justify-between gap-3 border-t border-border">
                  <div className="text-sm">
                    <div className="font-semibold">{event.venue_name}</div>
                    <div className="text-stone-500 text-xs">{event.venue_address}</div>
                  </div>
                  {event.google_maps_url && (
                    <a href={event.google_maps_url} target="_blank" rel="noreferrer" data-testid="event-map-open">
                      <Button size="sm" variant="outline"><MapPin size={14} className="mr-1" />Open in Maps</Button>
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          {(event.photos || []).length > 0 && (
            <div>
              <div className="eyebrow mb-3">Venue gallery</div>
              <PhotoCarousel photos={event.photos} />
            </div>
          )}
        </div>

        <aside className="lg:col-span-5">
          <div className="cgc-card p-6 sticky top-20">
            <div className="eyebrow mb-4">Select tickets</div>
            <div className="space-y-4">
              {event.tiers.map((t) => {
                const remaining = t.quantity - (t.sold || 0) - (t.reserved || 0);
                const soldOut = remaining <= 0;
                return (
                  <div key={t.id} className="flex items-center justify-between gap-3 border-b border-border pb-4 last:border-0" data-testid={`tier-row-${t.id}`}>
                    <div>
                      <div className="font-semibold">{t.name}</div>
                      {t.description && <div className="text-xs text-stone-500">{t.description}</div>}
                      <div className="text-sm font-mono mt-1">{t.price_paise === 0 ? "Free" : fmtINR(t.price_paise)}</div>
                      <div className="text-[11px] text-stone-500 mt-0.5">{soldOut ? "Sold out" : `${remaining} left`}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {soldOut ? (
                        <WaitlistDialog event={event} tier={t} />
                      ) : (
                        <>
                          <Button variant="outline" size="icon" data-testid={`tier-dec-${t.id}`} disabled={!qty[t.id]} onClick={() => setQ(t.id, (qty[t.id] || 0) - 1)}><Minus size={14} /></Button>
                          <div className="w-8 text-center font-mono" data-testid={`tier-qty-${t.id}`}>{qty[t.id] || 0}</div>
                          <Button variant="outline" size="icon" data-testid={`tier-inc-${t.id}`} disabled={soldOut || (qty[t.id] || 0) >= t.per_order_limit} onClick={() => setQ(t.id, (qty[t.id] || 0) + 1)}><Plus size={14} /></Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 flex gap-2">
              <input data-testid="coupon-input" placeholder="Coupon code" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} className="flex-1 border border-border rounded-md px-3 py-2 text-sm bg-background" />
            </div>

            <div className="mt-5 flex items-center justify-between text-sm">
              <span className="text-stone-600 dark:text-stone-400">Subtotal</span>
              <span className="font-mono font-semibold">{fmtINR(subtotal)}</span>
            </div>
            <p className="text-[11px] text-stone-500 mt-1">A checkout charge and any discount will be shown before payment.</p>
            <Button data-testid="reserve-continue-btn" onClick={reserve} disabled={loading || !hasItems} className="w-full mt-4 btn-brand h-11">
              {loading ? "Reserving…" : "Continue"}
            </Button>
          </div>
        </aside>
      </div>
    </PublicLayout>
  );
}

function PhotoCarousel({ photos }) {
  const [idx, setIdx] = useState(0);
  if (!photos || photos.length === 0) return null;
  const n = photos.length;
  const prev = () => setIdx((idx - 1 + n) % n);
  const next = () => setIdx((idx + 1) % n);
  return (
    <div className="cgc-card overflow-hidden" data-testid="photo-carousel">
      <div className="relative bg-stone-900">
        <img src={photos[idx]} alt="" className="w-full h-72 object-cover" />
        {n > 1 && (
          <>
            <button aria-label="Previous" onClick={prev} data-testid="carousel-prev" className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-background/85 border border-border flex items-center justify-center hover:bg-background">‹</button>
            <button aria-label="Next" onClick={next} data-testid="carousel-next" className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-background/85 border border-border flex items-center justify-center hover:bg-background">›</button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 bg-background/70 px-2 py-1 rounded-full">
              {photos.map((_, i) => (
                <button key={i} aria-label={`Photo ${i+1}`} onClick={() => setIdx(i)} className={`w-1.5 h-1.5 rounded-full ${i === idx ? "bg-[color:var(--brand-terracotta)]" : "bg-stone-400"}`} />
              ))}
            </div>
          </>
        )}
      </div>
      {n > 1 && (
        <div className="p-2 grid grid-cols-6 gap-1.5">
          {photos.map((p, i) => (
            <button key={i} onClick={() => setIdx(i)} data-testid={`carousel-thumb-${i}`}
              className={`aspect-[4/3] overflow-hidden rounded-md border ${i === idx ? "border-[color:var(--brand-terracotta)] ring-2 ring-[color:var(--brand-terracotta)]/30" : "border-border"}`}>
              <img src={p} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function WaitlistDialog({ event, tier }) {  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", phone: "" });
  const submit = async () => {
    if (!f.name || !f.email) return toast.error("Name and email required");
    try {
      const r = await api.post(`/events/${event.id}/waitlist`, { ...f, tier_id: tier.id });
      toast.success(`Added — you're #${r.data.position} on the waitlist`);
      setOpen(false); setF({ name: "", email: "", phone: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid={`waitlist-btn-${tier.id}`}>Join waitlist</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Waitlist · {tier.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-stone-500">We&apos;ll email you the moment a seat opens up.</p>
        <div className="space-y-3">
          <div><Label>Name</Label><Input data-testid="waitlist-name" value={f.name} onChange={(e) => setF({...f,name:e.target.value})} /></div>
          <div><Label>Email</Label><Input data-testid="waitlist-email" value={f.email} onChange={(e) => setF({...f,email:e.target.value})} /></div>
          <div><Label>Phone (optional)</Label><Input data-testid="waitlist-phone" value={f.phone} onChange={(e) => setF({...f,phone:e.target.value})} /></div>
        </div>
        <Button className="btn-brand" onClick={submit} data-testid="waitlist-submit">Join waitlist</Button>
      </DialogContent>
    </Dialog>
  );
}
